/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, inject, Input, OnChanges, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NwNumberErrorMemory } from '../../../../shared/models/nw-number/nw-number-error-memory';
import { LanguageApiService } from '../../../services/api/language-api.service';
import { LanguageDto } from '../../../../shared/models/info/language-dto';
import { ExcludeLanguagesForNwNumberErrorMemory } from '../../../../shared/models/nw-number/exclude-languages-for-nw-number-error-memory';
import { NwNumberErrorMemoryApiService } from '../../../services/api/nw-number-error-memory-api.service';
import { AccessRightsService } from '../../../services/access-rights.service';
import { Policy } from '../../../../shared/models/info/policy';
import { NgClass } from '@angular/common';
import { Observable } from 'rxjs';
import { TranslationService } from '../../../services/translation.service';
import { errorToString } from '../../../common/common-utils';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-language-configuration',
  imports: [NgClass],
  templateUrl: './language-configuration.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './language-configuration.component.scss',
})
export class LanguageConfigurationComponent implements OnInit, OnChanges {
  @Input() public nwNumberErrorMemory?: NwNumberErrorMemory;

  public selectedLanguages: Map<LanguageDto, boolean> = new Map<LanguageDto, boolean>();
  public noAccessToLanguageConfiguration = true;

  private readonly languageApi = inject(LanguageApiService);
  private readonly nwNumberErrorMemoryApi = inject(NwNumberErrorMemoryApiService);
  private readonly toastr = inject(HotToastService);
  private readonly accessRightsService = inject(AccessRightsService);

  public ngOnInit(): void {
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.noAccessToLanguageConfiguration = !ar.authorized(Policy.canUpdateTranslations);
    });
  }

  public ngOnChanges(): void {
    this.fetchAllLanguages();
  }

  public updateLanguageSelection(e: Event, languageCodeId?: string): void {
    const isChecked = (e.target as HTMLInputElement).checked;
    this.setLanguageSelection(languageCodeId ?? '', isChecked);
    this.excludeLanguages().subscribe({
      next: () => {
        this.toastr.success('Done');
      },
      error: (error) => {
        console.error('Error excluding languages:', errorToString(error));
        this.setLanguageSelection(languageCodeId ?? '', !isChecked);
      },
    });
  }

  private setLanguageSelection(languageCodeId: string, isChecked: boolean): void {
    this.selectedLanguages.forEach((value, key) => {
      if (key.languageCodeId === languageCodeId) {
        this.selectedLanguages.set(key, isChecked);
      }
    });
  }

  private fetchAllLanguages(): void {
    this.selectedLanguages.clear();
    this.languageApi.getLanguages().subscribe({
      next: (languages) => {
        languages
          .filter((lang) => lang.languageCodeId != TranslationService.defaultLanguageCode())
          .forEach((language) => {
            const isEnabled =
              !this.nwNumberErrorMemory?.filteredLanguages
                ?.map((l) => l.languageCodeId)
                .includes(language.languageCodeId) || false;
            this.selectedLanguages.set(language, isEnabled);
          });
      },
      error: (error) => {
        console.error('Error fetching languages:', errorToString(error));
      },
    });
  }

  private excludeLanguages(): Observable<void> {
    const excludeLanguages: ExcludeLanguagesForNwNumberErrorMemory = {
      nwNumberErrorMemoryId: this.nwNumberErrorMemory?.id || '',
      languagesToExclude: Array.from(this.selectedLanguages.entries())
        .filter(([_, isSelected]) => !isSelected)
        .map(([language, _]) => language),
      rowVersion: this.nwNumberErrorMemory?.rowVersion || 0,
    };
    return this.nwNumberErrorMemoryApi.excludeLanguages(excludeLanguages);
  }
}
